/**
 * iter327Z — HiddenCardsPanel
 *
 * Lists all dismissed UI cards/widgets so the user can restore them.
 * Reads localStorage keys written by NextStepCard, DailyRecommendationCard,
 * BackfillNudge (and other dismissibles). Removing the localStorage entries
 * makes those cards reappear on next render.
 *
 * Heuristic — only show panel when ≥ 3 hidden items (to reduce settings clutter
 * for first-day users). Always visible if user has at least 1 hidden item AND
 * `force=true` prop is passed.
 */
import React, { useState, useEffect, useCallback } from "react";
import { EyeOff, RotateCcw, Trash2 } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";

// Each entry describes one dismissible widget by the localStorage key it sets,
// plus a human label and a "test" function that decides whether the entry is
// currently dismissed.
const DISMISSIBLES = [
  {
    key: "nutramilo_daily_rec_hidden",
    label_bg: "Дневна AI препоръка",
    label_en: "Daily AI recommendation",
    isHidden: () => {
      try { return localStorage.getItem("nutramilo_daily_rec_hidden") === "1"; }
      catch { return false; }
    },
    restore: () => {
      try { localStorage.removeItem("nutramilo_daily_rec_hidden"); } catch {}
    },
  },
  {
    key: "backfill-nudge-dismissed",
    label_bg: "Покана за допълване на пропуснати дни",
    label_en: "Backfill missed-days nudge",
    isHidden: () => {
      try { return localStorage.getItem("backfill-nudge-dismissed") === "1"; }
      catch { return false; }
    },
    restore: () => {
      try { localStorage.removeItem("backfill-nudge-dismissed"); } catch {}
    },
  },
  {
    key: "nutramilo_dismissed_next_steps",
    label_bg: "Следваща стъпка (предложения)",
    label_en: "Next-step suggestions",
    isHidden: () => {
      try {
        const raw = localStorage.getItem("nutramilo_dismissed_next_steps");
        return !!(raw && JSON.parse(raw)?.length);
      } catch { return false; }
    },
    restore: () => {
      try { localStorage.removeItem("nutramilo_dismissed_next_steps"); } catch {}
    },
  },
  {
    key: "voice-fab-hint-v1",
    label_bg: "Подсказка за гласово записване",
    label_en: "Voice-logging hint",
    isHidden: () => {
      try { return localStorage.getItem("voice-fab-hint-v1") === "dismissed"; }
      catch { return false; }
    },
    restore: () => {
      try { localStorage.removeItem("voice-fab-hint-v1"); } catch {}
    },
  },
];

export default function HiddenCardsPanel({ minToShow = 3 }) {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const [hidden, setHidden] = useState([]);

  const refresh = useCallback(() => {
    setHidden(DISMISSIBLES.filter((d) => d.isHidden()));
  }, []);

  useEffect(() => {
    refresh();
    // Re-check on storage changes (multi-tab)
    const onStorage = () => refresh();
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [refresh]);

  // Heuristic threshold — hide entire panel until user has enough hidden cards
  // to make the setting meaningful (mimics Apple Health / Fitbit "Restore" UX).
  if (hidden.length < minToShow) return null;

  const restoreOne = (entry) => {
    entry.restore();
    refresh();
  };

  const restoreAll = () => {
    DISMISSIBLES.forEach((d) => d.restore());
    refresh();
    // Also force a re-render of cards by dispatching a generic event
    try { window.dispatchEvent(new Event("nutramilo:cards-restored")); } catch {}
  };

  return (
    <div
      className={`p-4 rounded-2xl ${isDark ? "bg-[hsl(223,47%,11%)] border border-white/5" : "bg-white border border-gray-100 shadow-sm"}`}
      data-testid="hidden-cards-panel"
    >
      <div className="flex items-start gap-2 mb-3">
        <EyeOff className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isDark ? "text-violet-400" : "text-violet-600"}`} />
        <div className="min-w-0">
          <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
            {bg ? "Скрити модули и подсказки" : "Hidden modules and hints"}
          </h3>
          <p className={`text-[11px] mt-0.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            {bg
              ? `Тук са картите, които си скрил. Можеш да ги възстановиш по всяко време.`
              : `Here are the cards you've dismissed. You can restore any of them.`}
          </p>
        </div>
      </div>

      <ul className="space-y-1.5 mb-3">
        {hidden.map((d) => (
          <li
            key={d.key}
            className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg ${
              isDark ? "bg-[hsl(223,47%,9%)] border border-white/5" : "bg-gray-50 border border-gray-100"
            }`}
          >
            <span className={`text-xs flex-1 truncate ${isDark ? "text-gray-200" : "text-gray-700"}`}>
              {bg ? d.label_bg : d.label_en}
            </span>
            <button
              onClick={() => restoreOne(d)}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors ${
                isDark
                  ? "bg-violet-500/20 text-violet-300 hover:bg-violet-500/30"
                  : "bg-violet-100 text-violet-700 hover:bg-violet-200"
              }`}
              data-testid={`restore-card-${d.key}`}
            >
              <RotateCcw className="w-3 h-3" />
              {bg ? "Покажи" : "Restore"}
            </button>
          </li>
        ))}
      </ul>

      <button
        onClick={restoreAll}
        className={`w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
          isDark
            ? "bg-violet-500 text-white hover:bg-violet-600"
            : "bg-violet-600 text-white hover:bg-violet-700"
        }`}
        data-testid="restore-all-cards-btn"
      >
        <Trash2 className="w-3.5 h-3.5" />
        {bg ? "Възстанови всички скрити карти" : "Restore all hidden cards"}
      </button>
    </div>
  );
}
